import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lightbulb, Facebook, Users, MapPin, Heart } from "lucide-react";
import { api } from "@/lib/api";
import { FB_MEMBERS_DISPLAY } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { useSeo } from "@/hooks/useSeo";

import HeroSearch from "@/components/home/HeroSearch";
import ParticiperVieLocale from "@/components/home/ParticiperVieLocale";
import ActiveDeals from "@/components/home/ActiveDeals";
import FeaturedPros from "@/components/home/FeaturedPros";
import LocalLife from "@/components/home/LocalLife";
import ProSpaceConversion from "@/components/home/ProSpaceConversion";
import ServicesBlock from "@/components/home/ServicesBlock";
import NewsletterCTA from "@/components/NewsletterCTA";

export default function Home() {
  const [content, setContent] = useState({});
  const [stats, setStats] = useState({ fb_groups: 0 });
  const [showStickyBar, setShowStickyBar] = useState(false);

  useSeo({
    title: "PROXIreduc-28 — la vie locale à portée de clic en Eure-et-Loir",
    description: "Commerces, artisans, bons plans, événements et services locaux près de chez vous en Eure-et-Loir. Créez votre compte gratuit et économisez localement.",
    canonicalPath: "/",
    image: "/og-image.jpg",
  });

  useEffect(() => {
    api.get("/public/site-content").then(({ data }) => setContent(data || {})).catch(() => {});
    api.get("/public/home-stats").then((r) => setStats(r.data || {})).catch(() => {});
  }, []);

  useEffect(() => {
    const onScroll = () => setShowStickyBar(window.scrollY > 700);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="bg-[#FAF8F5]">
      {/* ===== Barre d'annonce : suggestion locale ===== */}
      <div className="bg-[#0F0F11] text-white" data-testid="home-top-bar">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 min-h-11 py-2 flex items-center justify-center gap-3">
          <Link to="/suggestions" data-testid="home-suggestion-banner" className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3.5 py-1.5 text-[11px] leading-tight text-white font-medium hover:bg-white/20 hover:border-white/35 transition-colors">
            <Lightbulb size={14} className="shrink-0 text-[#E05638]" />
            <span>Un commerce, une association, une idée ? Proposez-la ! 🧡 <span className="underline underline-offset-2">Cliquez ici</span></span>
          </Link>
        </div>
      </div>

      {/* 2 · Hero recherche locale */}
      <HeroSearch content={content} stats={stats} />

      {/* Bandeau réassurance (chiffres réels uniquement) */}
      <div className="bg-[#0F0F11] text-white" data-testid="home-reassurance">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm font-semibold">
          {stats.fb_groups > 0 && (
            <span className="inline-flex items-center gap-1.5" data-testid="reassurance-groups">
              <Facebook size={15} className="text-[#E05638]" /> Déjà {stats.fb_groups_display || stats.fb_groups} groupes locaux
            </span>
          )}
          <span className="hidden sm:inline text-white/20">·</span>
          <span className="inline-flex items-center gap-1.5" data-testid="reassurance-members">
            <Users size={15} className="text-[#E05638]" /> {stats.fb_members_display || FB_MEMBERS_DISPLAY} membres en cumulé
          </span>
          <span className="hidden sm:inline text-white/20">·</span>
          <span className="inline-flex items-center gap-1.5" data-testid="reassurance-local">
            <MapPin size={15} className="text-[#E05638]" /> {stats.reassurance_text || "100 % local · Eure-et-Loir"}
          </span>
        </div>
      </div>

      {/* Nos services — bloc regroupé (sous le hero) */}
      <ServicesBlock />

      {/* 4 · Participez à la vie locale — actions + inscription + univers compacts */}
      <ParticiperVieLocale />

      {/* 4 · Bons plans du moment */}
      <ActiveDeals />

      {/* 5 · Professionnels à découvrir */}
      <FeaturedPros />

      {/* 7 · Vie locale (événements, clubs, associations) */}
      <LocalLife />

      {/* 10 · Espace professionnels (offre Fondateur) */}
      <ProSpaceConversion />

      {/* 11 · Newsletter */}
      <section className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-10" data-testid="newsletter-section">
        <NewsletterCTA source="home" compact />
      </section>

      {/* Barre CTA sticky mobile */}
      {showStickyBar && (
        <div className="fixed inset-x-0 z-40 lg:hidden bg-white/95 backdrop-blur border-t border-slate-200 p-3 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] bottom-[calc(64px+env(safe-area-inset-bottom))]" data-testid="home-sticky-cta">
          <Link to="/inscription">
            <Button className="w-full h-12 btn-shine text-white font-extrabold rounded-xl whitespace-normal leading-tight" data-testid="home-sticky-register">
              <Heart size={18} className="mr-2" /> Je profite des offres — c'est gratuit
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
}
